import React, { useEffect, useState, useRef } from 'react'
import { useLessonStore } from '@/store/lessonStore'
import { apiClient } from '@/services/api'
import {
  BarChart3,
  Users,
  Clock,
  Target,
  Activity,
  Award,
  Zap
} from 'lucide-react'

interface LessonAnalyticsProps {
  expanded?: boolean
}

interface OverviewData {
  basicStats: {
    status: string
    playerCount: number
    activityCount: number
    duration: number
  }
  engagement: {
    responseRate: number
    averageSessionDuration: number
    playerRetention: number
    activityCompletionRate: number
  }
  activityPerformance: Array<{
    title: string
    responseRate: number
    averageResponseTime: number
    difficultyScore: number
  }>
  playerDistribution: {
    byConnection: { connected: number; disconnected: number }
  }
}

export function LessonAnalytics({ expanded = false }: LessonAnalyticsProps) {
  const { currentLesson, currentActivity } = useLessonStore()
  const [overview, setOverview] = useState<OverviewData | null>(null)
  const pollRef = useRef<NodeJS.Timeout | null>(null)

  const fetchOverview = async () => {
    if (!currentLesson?.id) return
    try {
      const res = await apiClient.getLessonOverview(currentLesson.id)
      if (res.success && res.data) {
        setOverview(res.data as any)
      }
    } catch {
      // Silently ignore
    }
  }

  useEffect(() => {
    fetchOverview()
    pollRef.current = setInterval(fetchOverview, 5000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [currentLesson?.id])

  const StatCard = ({
    icon: Icon,
    label,
    value,
    subValue,
    color = 'blue',
  }: {
    icon: any
    label: string
    value: string | number
    subValue?: string
    color?: 'blue' | 'green' | 'yellow' | 'purple' | 'red'
  }) => {
    const accentClasses = {
      blue: 'text-blue-500 border-blue-500/30',
      green: 'text-green-500 border-green-500/30',
      yellow: 'text-yellow-500 border-yellow-500/30',
      purple: 'text-purple-500 border-purple-500/30',
      red: 'text-red-500 border-red-500/30'
    }

    return (
      <div className={`p-4 rounded-lg border bg-surface ${accentClasses[color]}`}>
        <div className="flex items-center justify-between mb-2">
          <Icon className="w-5 h-5" />
        </div>
        <div className="text-2xl font-bold mb-1">{value}</div>
        <div className="text-sm text-theme-text-muted">{label}</div>
        {subValue && <div className="text-xs text-theme-muted mt-1">{subValue}</div>}
      </div>
    )
  }

  const responseRate = overview?.engagement.responseRate ?? 0
  const completionRate = overview?.engagement.activityCompletionRate ?? 0
  const retention = overview?.engagement.playerRetention ?? 0
  const totalPlayers = overview?.basicStats.playerCount ?? 0
  const connected = overview?.playerDistribution?.byConnection?.connected ?? 0
  const totalActivities = overview?.basicStats.activityCount ?? 0

  // Compute average response time across all activities
  const perfEntries = overview?.activityPerformance?.filter(a => a.averageResponseTime > 0) || []
  const avgResponseTime = perfEntries.length > 0
    ? perfEntries.reduce((sum, a) => sum + a.averageResponseTime, 0) / perfEntries.length
    : 0

  return (
    <div className="bg-surface-deep rounded-lg p-6">
      <div className="flex items-center gap-2 mb-6">
        <BarChart3 className="w-5 h-5 text-[var(--color-primary)]" />
        <h3 className="ds-section-title">Live Analytics</h3>
      </div>

      {/* Key metrics grid */}
      <div className={`grid gap-4 mb-6 ${expanded ? 'grid-cols-4' : 'grid-cols-2'}`}>
        <StatCard
          icon={Users}
          label="Active Players"
          value={connected}
          subValue={`${totalPlayers} total`}
          color="blue"
        />

        <StatCard
          icon={Target}
          label="Response Rate"
          value={`${Math.round(responseRate)}%`}
          subValue="Across all activities"
          color={responseRate >= 70 ? 'green' : responseRate >= 50 ? 'yellow' : 'red'}
        />

        <StatCard
          icon={Zap}
          label="Player Retention"
          value={`${Math.round(retention)}%`}
          subValue={`${connected}/${totalPlayers} still connected`}
          color={retention >= 70 ? 'green' : retention >= 50 ? 'yellow' : 'red'}
        />

        <StatCard
          icon={Clock}
          label="Avg Response Time"
          value={`${Math.round(avgResponseTime)}s`}
          subValue="Across all activities"
          color="purple"
        />

        {expanded && (
          <>
            <StatCard
              icon={Activity}
              label="Total Activities"
              value={totalActivities}
              subValue={`${overview?.activityPerformance?.filter((_, i) => {
                const acts = currentLesson?.activities
                return acts && acts[i]?.status === 'completed'
              }).length ?? 0} completed`}
              color="blue"
            />

            <StatCard
              icon={Award}
              label="Completion Rate"
              value={`${Math.round(completionRate)}%`}
              subValue="Activities completed"
              color="green"
            />
          </>
        )}
      </div>

      {/* Per-activity performance list (expanded mode) */}
      {expanded && overview?.activityPerformance && overview.activityPerformance.length > 0 && (
        <div className="mb-6">
          <h4 className="font-medium mb-3 text-sm">Activity Performance</h4>
          <div className="space-y-2">
            {overview.activityPerformance.map((activity, idx) => {
              const isActive = currentLesson?.activities?.[idx]?.id === currentActivity?.id
              return (
                <div
                  key={idx}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg border text-sm ${
                    isActive
                      ? 'bg-blue-500/10 border-blue-500/30'
                      : 'bg-surface border-transparent'
                  }`}
                >
                  <span className="text-xs text-theme-text-muted w-5 text-right shrink-0">{idx + 1}</span>
                  {isActive && <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse shrink-0" />}
                  <span className="flex-1 truncate font-medium">{activity.title}</span>
                  {activity.responseRate > 0 ? (
                    <>
                      <span className="text-xs text-theme-text-muted shrink-0">{Math.round(activity.responseRate)}% responses</span>
                      <span className="text-xs text-theme-text-muted shrink-0">{Math.round(activity.averageResponseTime)}s avg</span>
                    </>
                  ) : (
                    <span className="text-xs text-theme-text-muted">—</span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Current activity stats */}
      {currentActivity && (
        <div className="mb-6">
          <h4 className="font-medium mb-3">Current Activity</h4>
          <div className="bg-surface rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium">{(currentActivity.activity_data as any)?.question || (currentActivity.activity_data as any)?.title || currentActivity.title}</span>
              <span className="text-sm text-theme-text-muted">
                {currentActivity.response_count || 0} responses
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Real-time activity indicator */}
      <div className="flex items-center justify-center text-sm text-theme-text-muted">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          <span>Live data updating</span>
        </div>
      </div>
    </div>
  )
}
